import { useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  invalidateSpaceScopedQueries,
  spacesListQueryOptions,
} from "@/modules/spaces/spaces-queries";
import { useSpaceStore, RECENT_SPACES_VISIBLE } from "@/modules/spaces/space-store";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";
import { sessionsQueryOptions } from "../queries/session-queries";
import { useChatStore } from "../stores";
import {
  AgentsIcon,
  CommandsIcon,
  CompanyIcon,
  ConnectorsIcon,
  HistoryIcon,
  LogoutIcon,
  NewChatIcon,
  RoutinesIcon,
  SettingsIcon,
  SidebarToggleIcon,
  SkillsIcon,
} from "@/shared/icons";
import { ModelOverrideDialog } from "@/shared/ui/ModelOverrideDialog";

interface Props {
  onStartNew: () => void;
  userName: string;
  isAdmin: boolean;
  onLogout: () => void;
  onOpenControl: () => void;
  collapsed?: boolean;
  onToggleSidebar?: () => void;
}

// The visible-limit constant lives in space-store (the store needs it
// to decide whether a clicked space is on-screen and should not
// reshuffle). Aliased locally to keep the rest of this file readable.
const RECENT_SPACES_LIMIT = RECENT_SPACES_VISIBLE;

export function Sidebar({
  onStartNew,
  userName,
  isAdmin,
  onLogout,
  onOpenControl,
  collapsed = false,
  onToggleSidebar,
}: Props) {
  const [profileMenu, setProfileMenu] = useState(false);
  const [extensionsOpen, setExtensionsOpen] = useState(false);
  const [spacesOpen, setSpacesOpen] = useState(true);
  // Hidden model switcher: tap the logo 10× within 3s to open. Counter
  // resets if you pause — prevents an accidental click streak over the
  // span of a session from triggering it.
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const logoTapsRef = useRef<number>(0);
  const logoTapResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleLogoTap = () => {
    logoTapsRef.current += 1;
    if (logoTapResetRef.current) clearTimeout(logoTapResetRef.current);
    logoTapResetRef.current = setTimeout(() => {
      logoTapsRef.current = 0;
    }, 3000);
    if (logoTapsRef.current >= 10) {
      logoTapsRef.current = 0;
      if (logoTapResetRef.current) clearTimeout(logoTapResetRef.current);
      setModelDialogOpen(true);
    }
  };
  const initial = (userName || '?').trim()[0]?.toUpperCase() || '?';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { pathname } = useLocation();
  // Self-read of chat state — used to decide whether the «Новая задача»
  // entry shows as the active default-home pill. ChatPage and other
  // hosts no longer need to thread these through as props.
  const activeId = useChatStore((s) => s.activeSessionId);
  const pendingAgentRef = useAgentDraftStore((s) => s.pendingRef);
  // True while a draft from SpaceDashboard is being turned into a session —
  // suppresses the «Новая задача» pill flash during navigate→createSession.
  const starting = useAgentDraftStore((s) => s.starting);
  // HITL counter — sessions where the agent is parked on a frontend tool
  // call awaiting the user's reply. Surfaced as an orange badge on the
  // «История» nav item.
  const { data: sessions = [] } = useQuery(sessionsQueryOptions());
  const awaitingCount = sessions.reduce(
    (n, s) => (s.awaiting_user_input ? n + 1 : n),
    0,
  );
  const { data: spaces = [] } = useQuery(spacesListQueryOptions());
  const currentSpaceId = useSpaceStore((s) => s.currentSpaceId);
  const setCurrentSpaceId = useSpaceStore((s) => s.setCurrentSpaceId);
  const recentSpaceIds = useSpaceStore((s) => s.recentSpaceIds);
  // Active section is derived from the URL — the URL is the single source
  // of truth for "where am I". Match by prefix so future nested routes
  // (e.g. /agents/$ref) keep their parent entry highlighted.
  const isOn = (prefix: string) => pathname === prefix || pathname.startsWith(prefix + "/");
  const companyOpen = isOn("/company");
  const historyOpen = isOn("/history");
  const agentsOpen = isOn("/agents");
  const skillsOpen = isOn("/skills");
  const commandsOpen = isOn("/commands");
  const connectorsOpen = isOn("/connectors");
  const routinesOpen = isOn("/routines");
  // «Показать все пространства» — каталог по `/spaces` (без `$id`). Точное
  // равенство, чтобы на `/spaces/$id` подсвечивался конкретный space, а не
  // общий пункт каталога.
  const spacesIndexOpen = pathname === "/spaces" || pathname === "/spaces/";
  // Highlighted space is derived from the URL, NOT the space store. The
  // store's `currentSpaceId` is the working tenancy for `X-Space-Id` and
  // stays sticky as the user navigates between sections — using it for
  // the highlight kept the pill lit on /history, /agents, etc. The pill
  // should only glow while the user is actually viewing that space.
  const spacePathMatch = pathname.match(/^\/spaces\/([^/]+)/);
  const activeSpaceFromUrl = spacePathMatch?.[1] ?? null;

  // Default-home highlight: no session, no agent override, on /. The
  // session-bound agent doesn't matter here — when activeId is set,
  // isHero is already false.
  const isDefaultHero =
    activeId === null &&
    pendingAgentRef === null &&
    pathname === "/" &&
    !starting;

  // Recent spaces: MRU-first using the locally tracked recentSpaceIds,
  // backfilled from the server list for fresh installs / spaces the user
  // hasn't visited yet. Server already filters out personal at SQL level
  // and orders memberships first — that's a sensible fallback ordering.
  const recentSpaces = (() => {
    const byId = new Map(spaces.map((s) => [s.id, s]));
    const mru = recentSpaceIds
      .map((id) => byId.get(id))
      .filter((s): s is NonNullable<typeof s> => !!s);
    const filled = [...mru];
    for (const s of spaces) {
      if (filled.length >= RECENT_SPACES_LIMIT) break;
      if (!filled.some((x) => x.id === s.id)) filled.push(s);
    }
    return filled.slice(0, RECENT_SPACES_LIMIT);
  })();

  const switchToSpace = (id: string) => {
    if (id === currentSpaceId) return;
    setCurrentSpaceId(id);
    invalidateSpaceScopedQueries(qc);
  };

  return (
    <div className={`sidebar sidebar-left${collapsed ? ' sidebar-left--collapsed' : ''}`}>
      <div className="sidebar-top">
        <div className="sidebar-head">
          <img
            src="/logo.png"
            alt="GIGA COWORK"
            className="sidebar-logo-img"
            onClick={handleLogoTap}
          />
          <button
            type="button"
            className="sidebar-toggle-btn"
            onClick={onToggleSidebar}
            title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          >
            <SidebarToggleIcon className="sidebar-nav-icon" />
          </button>
        </div>

        {isAdmin && (
          <NavItem
            icon={<CompanyIcon />}
            label="Компания"
            active={companyOpen}
            onClick={() => navigate({ to: "/company" })}
          />
        )}
        <NavItem
          icon={<NewChatIcon />}
          label="Новая задача"
          active={isDefaultHero}
          onClick={onStartNew}
        />
        <NavItem
          icon={<HistoryIcon />}
          label="История"
          active={historyOpen}
          onClick={() => navigate({ to: "/history" })}
          counter={awaitingCount}
        />
        <NavItem
          icon={<RoutinesIcon />}
          label="Автоматизации"
          active={routinesOpen}
          onClick={() => navigate({ to: "/routines" })}
        />

        <div className="sidebar-extensions">
          <button
            type="button"
            className="sidebar-extensions-head"
            onClick={() => setSpacesOpen((v) => !v)}
            aria-expanded={spacesOpen}
          >
            <span>Недавние пространства</span>
            <svg
              className={`sidebar-chevron${spacesOpen ? ' sidebar-chevron--open' : ''}`}
              width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          <div
            className={`sidebar-collapsible${spacesOpen ? ' sidebar-collapsible--open' : ''}`}
            aria-hidden={!spacesOpen}
          >
            <div>
              <div className="sidebar-extensions-body">
                <div className="sidebar-recent-spaces-list">
                  {recentSpaces.map((s) => (
                    <NavItem
                      key={s.id}
                      icon={<TileIcon />}
                      label={s.name}
                      active={s.id === activeSpaceFromUrl}
                      onClick={() => {
                        switchToSpace(s.id);
                        navigate({ to: "/spaces/$id", params: { id: s.id } });
                      }}
                    />
                  ))}
                </div>
                <NavItem
                  icon={<StackIcon />}
                  label="Показать все пространства"
                  active={spacesIndexOpen}
                  muted
                  onClick={() => navigate({ to: "/spaces" })}
                />
                <NavItem
                  icon={<PlusIcon />}
                  label="Создать новое пространство"
                  muted
                  onClick={() => navigate({ to: "/spaces", search: { new: true } })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sidebar-bottom">
        <div className="sidebar-extensions">
          <button
            type="button"
            className="sidebar-extensions-head"
            onClick={() => setExtensionsOpen((v) => !v)}
            aria-expanded={extensionsOpen}
          >
            <span>Расширения</span>
            <svg
              className={`sidebar-chevron${extensionsOpen ? ' sidebar-chevron--open' : ''}`}
              width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          <div
            className={`sidebar-collapsible${extensionsOpen ? ' sidebar-collapsible--open' : ''}`}
            aria-hidden={!extensionsOpen}
          >
            <div>
              <div className="sidebar-extensions-body">
                <NavItem
                  icon={<AgentsIcon />}
                  label="Агенты"
                  active={agentsOpen}
                  onClick={() => navigate({ to: "/agents" })}
                />
                <NavItem
                  icon={<ConnectorsIcon />}
                  label="Коннекторы"
                  active={connectorsOpen}
                  onClick={() => navigate({ to: "/connectors" })}
                />
                <NavItem
                  icon={<SkillsIcon />}
                  label="Навыки"
                  active={skillsOpen}
                  onClick={() => navigate({ to: "/skills" })}
                />
                <NavItem
                  icon={<CommandsIcon />}
                  label="Команды"
                  active={commandsOpen}
                  onClick={() => navigate({ to: "/commands" })}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="sidebar-bottom-divider" />

        <button className="sidebar-item sidebar-item--settings" onClick={onOpenControl}>
          <SettingsIcon className="sidebar-nav-icon" />
          <span>Панель управления</span>
        </button>
        <div className="sidebar-profile-wrap">
          {profileMenu && (
            <>
              <div className="sidebar-profile-backdrop" onClick={() => setProfileMenu(false)} />
              <div className="sidebar-profile-menu">
                <button
                  className="sidebar-profile-menu-item sidebar-profile-menu-danger"
                  onClick={() => { setProfileMenu(false); onLogout(); }}
                >
                  <LogoutIcon className="sidebar-profile-menu-icon sidebar-nav-icon" />
                  <span>Выйти</span>
                </button>
              </div>
            </>
          )}
          <button
            className={`sidebar-item sidebar-item--profile${profileMenu ? ' active' : ''}`}
            onClick={() => setProfileMenu((p) => !p)}
          >
            <span className="sidebar-avatar">{initial}</span>
            <span className="sidebar-item-name">{userName}</span>
          </button>
        </div>
      </div>
      {modelDialogOpen && (
        <ModelOverrideDialog onClose={() => setModelDialogOpen(false)} />
      )}
    </div>
  );
}

function NavItem({
  icon,
  label,
  active,
  disabled,
  muted,
  onClick,
  counter,
}: {
  icon: ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  /** Faded appearance for secondary/ghost actions like «Показать все»,
   *  «Создать новое» — half-opacity text per Figma `text/text/03`. */
  muted?: boolean;
  onClick?: () => void;
  counter?: number;
}) {
  const showCounter = !!counter && counter > 0;
  return (
    <button
      type="button"
      className={`sidebar-item${active ? ' active' : ''}${disabled ? ' sidebar-item--disabled' : ''}${muted ? ' sidebar-item--muted' : ''}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? 'Скоро' : undefined}
    >
      {icon !== null && <span className="sidebar-nav-icon">{icon}</span>}
      <span className="sidebar-item-name">{label}</span>
      {showCounter && (
        <span className="sidebar-item-counter" aria-label={`${counter} требует действия`}>
          {counter > 99 ? '99+' : counter}
        </span>
      )}
    </button>
  );
}

function StackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 22 7 12 12 2 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function TileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
      <path d="M10 5C10.328 5.00005 10.6527 5.06994 10.9512 5.20605L18.1094 8.46484H18.1084C18.3644 8.5792 18.5822 8.76505 18.7354 9C18.8896 9.23672 18.9716 9.51338 18.9717 9.7959C18.9716 10.0784 18.8896 10.3551 18.7354 10.5918C18.582 10.827 18.3637 11.0126 18.1074 11.127L18.1084 11.1279L10.958 14.376L10.959 14.377C10.6607 14.513 10.3367 14.5839 10.0088 14.584C9.68072 14.584 9.35611 14.5131 9.05762 14.377L1.90723 11.1191V11.1182C1.65178 11.0038 1.43419 10.8185 1.28125 10.584C1.12682 10.3471 1.04492 10.0699 1.04492 9.78711C1.045 9.5045 1.12694 9.22798 1.28125 8.99121C1.43442 8.75631 1.65225 8.5704 1.9082 8.45605L9.04883 5.20605L9.16211 5.1582C9.42854 5.05356 9.71303 5.00005 10 5ZM10 6.25C9.85097 6.25005 9.70298 6.2819 9.56738 6.34375L2.42578 9.59375L2.41895 9.59668C2.38215 9.61292 2.35115 9.64019 2.3291 9.67383C2.30717 9.70753 2.295 9.7469 2.29492 9.78711C2.29492 9.82751 2.30704 9.86752 2.3291 9.90137C2.34002 9.91803 2.35316 9.9335 2.36816 9.94629L2.41895 9.97754L2.42578 9.98145L9.5752 13.2393H9.57617C9.71182 13.3011 9.85972 13.334 10.0088 13.334C10.1577 13.3339 10.3049 13.301 10.4404 13.2393H10.4414L17.5918 9.98926L17.5977 9.98633C17.6346 9.97002 17.6664 9.94299 17.6885 9.90918C17.7103 9.87555 17.7216 9.836 17.7217 9.7959C17.7216 9.75582 17.7103 9.71625 17.6885 9.68262C17.6664 9.64882 17.6346 9.62178 17.5977 9.60547L17.5908 9.60254L10.4326 6.34375L10.3291 6.30273C10.2232 6.26765 10.1119 6.25004 10 6.25Z" fill="currentColor" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
